import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CloneQuestionnaireInput,
  CreateQuestionnaireInput,
  CreateQuestionnaireQuestionInput,
  CreateQuestionnaireSectionInput,
  Questionnaire,
  QuestionnaireAuditEntry,
  QuestionnaireListItem,
  UpdateQuestionnaireInput,
  UpdateQuestionnaireQuestionInput,
  UpdateQuestionnaireSectionInput,
} from '@peles/shared';
import * as api from '../api/questionnaires';
import type { QuestionnaireListFilters } from '../api/questionnaires';

export function useQuestionnaires(filters?: QuestionnaireListFilters) {
  return useQuery<QuestionnaireListItem[]>({
    queryKey: ['questionnaires', filters ?? {}],
    queryFn: () => api.listQuestionnaires(filters),
  });
}

export function useQuestionnaire(id: string | undefined) {
  return useQuery<Questionnaire>({
    queryKey: ['questionnaires', 'detail', id],
    queryFn: () => api.getQuestionnaire(id!),
    enabled: !!id,
  });
}

export function useQuestionnaireAudit(id: string | undefined, enabled = true) {
  return useQuery<QuestionnaireAuditEntry[]>({
    queryKey: ['questionnaires', 'audit', id],
    queryFn: () => api.getQuestionnaireAudit(id!),
    enabled: !!id && enabled,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
    if (id) {
      queryClient.invalidateQueries({ queryKey: ['questionnaires', 'detail', id] });
    }
  };
}

export function useCreateQuestionnaire() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreateQuestionnaireInput) => api.createQuestionnaire(input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateQuestionnaire() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateQuestionnaireInput & { id: string }) =>
      api.updateQuestionnaire(id, input),
    onSuccess: (_data, variables) => invalidate(variables.id),
  });
}

export function useDeleteQuestionnaire() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.deleteQuestionnaire(id),
    onSuccess: () => invalidate(),
  });
}

export function useCloneQuestionnaire() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...input }: CloneQuestionnaireInput & { id: string }) =>
      api.cloneQuestionnaire(id, input),
    onSuccess: () => {
      invalidate();
      // Cloning may auto-provision a timeline and event tags on the event.
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useSaveQuestionnaireAsTemplate() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, templateName }: { id: string; templateName: string }) =>
      api.saveQuestionnaireAsTemplate(id, templateName),
    onSuccess: () => invalidate(),
  });
}

export function useSendQuestionnaireLink() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, email }: { id: string; email?: string }) =>
      api.sendQuestionnaireLink(id, email),
    onSuccess: (_data, variables) => invalidate(variables.id),
  });
}

export function useAddQuestionnaireSection() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      questionnaireId,
      ...input
    }: CreateQuestionnaireSectionInput & { questionnaireId: string }) =>
      api.addQuestionnaireSection(questionnaireId, input),
    onSuccess: (_data, variables) => invalidate(variables.questionnaireId),
  });
}

export function useUpdateQuestionnaireSection(questionnaireId?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      sectionId,
      ...input
    }: UpdateQuestionnaireSectionInput & { sectionId: string }) =>
      api.updateQuestionnaireSection(sectionId, input),
    onSuccess: () => invalidate(questionnaireId),
  });
}

export function useDeleteQuestionnaireSection(questionnaireId?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (sectionId: string) => api.deleteQuestionnaireSection(sectionId),
    onSuccess: () => invalidate(questionnaireId),
  });
}

export function useAddQuestionnaireQuestion(questionnaireId?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      sectionId,
      ...input
    }: CreateQuestionnaireQuestionInput & { sectionId: string }) =>
      api.addQuestionnaireQuestion(sectionId, input),
    onSuccess: () => invalidate(questionnaireId),
  });
}

export function useUpdateQuestionnaireQuestion(questionnaireId?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      questionId,
      ...input
    }: UpdateQuestionnaireQuestionInput & { questionId: string }) =>
      api.updateQuestionnaireQuestion(questionId, input),
    onSuccess: () => invalidate(questionnaireId),
  });
}

export function useDeleteQuestionnaireQuestion(questionnaireId?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (questionId: string) => api.deleteQuestionnaireQuestion(questionId),
    onSuccess: () => invalidate(questionnaireId),
  });
}

export function useApplyQuestionnaireAnswer(questionnaireId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) => api.applyQuestionnaireAnswer(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
      if (questionnaireId) {
        queryClient.invalidateQueries({ queryKey: ['questionnaires', 'detail', questionnaireId] });
      }
      // Applied answers mutate event data — refresh the common consumers.
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
      queryClient.invalidateQueries({ queryKey: ['entity-links'] });
    },
  });
}
